import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import dayjs from "dayjs";
import axios from "axios";
import InstagramScheduler from "../InstagramScheduler";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

export default function SchedulerPage({ token, pageId }) {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scheduled, setScheduled] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [isMock, setIsMock] = useState(false);
  const [mockScheduled, setMockScheduled] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editCaption, setEditCaption] = useState("");
  const [editTime, setEditTime] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!token || !pageId) return;
    const fetchScheduled = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = res.data || [];
        setScheduled(data);
        if (!data || data.length === 0) {
          // create lightweight mock data for the calendar
          const base = dayjs();
          const mocks = [
            { offset: 1, caption: "New Arrival Teaser", count: 1 },
            { offset: 3, caption: "Behind the Scenes", count: 2 },
            { offset: 8, caption: "Weekend Sale", count: 1 },
          ].flatMap((m) =>
            Array.from({ length: m.count }).map((_, i) => ({
              _id: `mock-${m.offset}-${i}`,
              caption: m.caption,
              scheduledTime: base.add(m.offset, "day").toISOString(),
              status: "PENDING",
              mediaUrl: "/placeholder-image.png",
            }))
          );
          setMockScheduled(mocks);
          setIsMock(true);
        } else {
          setIsMock(false);
          setMockScheduled([]);
        }
      } catch (e) {
        setError(
          e?.response?.data?.error ||
            e?.message ||
            "Failed to load scheduled posts"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchScheduled();
  }, [token, pageId]);

  const displayScheduled = isMock ? mockScheduled : scheduled;

  const postsByDate = useMemo(() => {
    const map = new Map();
    (displayScheduled || []).forEach((p) => {
      const d = dayjs(p.scheduledTime).format("YYYY-MM-DD");
      map.set(d, (map.get(d) || 0) + 1);
    });
    return map;
  }, [displayScheduled]);

  const itemsByDate = useMemo(() => {
    const map = new Map();
    (displayScheduled || []).forEach((p) => {
      const d = dayjs(p.scheduledTime).format("YYYY-MM-DD");
      const arr = map.get(d) || [];
      arr.push(p);
      map.set(d, arr);
    });
    return map;
  }, [displayScheduled]);

  const startOfMonth = currentMonth.startOf("month");
  const endOfMonth = currentMonth.endOf("month");
  const startGrid = startOfMonth.startOf("week");
  const endGrid = endOfMonth.endOf("week");
  const days = [];
  let cursor = startGrid;
  while (cursor.isBefore(endGrid) || cursor.isSame(endGrid, "day")) {
    days.push(cursor);
    cursor = cursor.add(1, "day");
  }

  const openDayDialog = (d) => {
    const key = d.format("YYYY-MM-DD");
    const items = (displayScheduled || []).filter((p) =>
      dayjs(p.scheduledTime).isSame(key, "day")
    );
    if (items.length === 0) return;
    setDialogDate(d);
    setEditingPost(null);
    setDialogOpen(true);
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/scheduled-post/${postId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Scheduled post deleted");
      // refresh list
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScheduled(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to delete post");
    }
  };

  const handlePublishNow = async (postId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/publish-scheduled-post/${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Post published");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScheduled(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to publish post");
    }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setEditCaption(post.caption || "");
    setEditTime(new Date(post.scheduledTime));
  };

  const saveEdit = async () => {
    if (!editingPost) return;
    setSavingEdit(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/scheduled-post/${editingPost._id}`,
        {
          caption: editCaption,
          scheduledTime:
            editTime?.toISOString?.() || new Date(editTime).toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Scheduled post updated");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScheduled(res.data || []);
      setEditingPost(null);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update post");
    } finally {
      setSavingEdit(false);
    }
  };

  if (showForm) {
    return (
      <Box>
        <Box sx={{ mb: 1 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => setShowForm(false)}
          >
            Back
          </Button>
        </Box>
        <InstagramScheduler
          token={token}
          pageId={pageId}
          showHeader={true}
          onScheduled={async () => {
            console.log("onScheduled callback triggered");
            setShowForm(false);
            // Refresh calendar after scheduling
            setLoading(true);
            setError("");
            try {
              console.log("Fetching updated scheduled posts...");
              const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log("Updated scheduled posts:", res.data);
              setScheduled(res.data || []);
              setIsMock(false); // Ensure we're not showing mock data
            } catch (e) {
              console.error("Error refreshing scheduled posts:", e);
              setError(
                e?.response?.data?.error ||
                  e?.message ||
                  "Failed to load scheduled posts"
              );
            } finally {
              setLoading(false);
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={isMock ? 0 : 2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarMonthIcon className="text-primary-600 dark:text-primary-400" />
          <Typography variant="h6" className="text-xl font-bold text-gray-900 dark:text-white">
            Scheduled Posts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => setShowForm(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
        >
          Schedule Post
        </Button>
      </Box>

      {isMock && (
        <div className="mt-4 mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
          <Typography className="text-sm text-primary-800 dark:text-primary-200">
            ℹ️ Showing mock scheduled posts as placeholders. This will disappear once
            you schedule a real post.
          </Typography>
        </div>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error && (
        <div className="mt-4 mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
          <Typography className="text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ {error}
          </Typography>
        </div>
      )}

      <div className="card-modern p-6">
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
          >
            Prev
          </Button>
          <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white">
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <Button
            onClick={() => setCurrentMonth((m) => m.add(1, "month"))}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
          >
            Next
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center">
              <Typography variant="caption" className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                {d}
              </Typography>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days.map((d) => {
            const inMonth = d.month() === currentMonth.month();
            const key = d.format("YYYY-MM-DD");
            const count = postsByDate.get(key) || 0;
            const items = itemsByDate.get(key) || [];
            const isToday = d.isSame(dayjs(), "day");
            return (
              <div
                key={key}
                className={`p-2 h-24 rounded-xl border transition-all duration-200 ${
                  inMonth
                    ? "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    : "bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800 opacity-50"
                } ${
                  count > 0
                    ? "cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md"
                    : "cursor-default"
                } ${
                  isToday
                    ? "ring-2 ring-primary-400 dark:ring-primary-600"
                    : ""
                }`}
                onClick={() => count > 0 && openDayDialog(d)}
              >
                <Typography
                  variant="caption"
                  className={`text-xs font-medium ${
                    isToday
                      ? "text-primary-600 dark:text-primary-400"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  {d.date()}
                </Typography>
                <div className="flex-1 flex items-center justify-center mt-1">
                  {count > 0 && (
                    <Tooltip
                      arrow
                      title={
                        <Box>
                          {items.slice(0, 5).map((p) => (
                            <Typography
                              key={p._id}
                              variant="caption"
                              display="block"
                            >
                              {(p.caption || "(no caption)").slice(0, 40)} —{" "}
                              {new Date(p.scheduledTime).toLocaleTimeString()}
                            </Typography>
                          ))}
                          {items.length > 5 && (
                            <Typography variant="caption">
                              +{items.length - 5} more…
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Chip
                        size="small"
                        label={`${count} scheduled`}
                        onClick={() => count > 0 && openDayDialog(d)}
                        className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-900/50"
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          className: "bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
        }}
      >
        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
          {dialogDate
            ? `Posts for ${dialogDate.format("MMM D, YYYY")}`
            : "Posts"}
        </DialogTitle>
        <DialogContent className="px-6 py-4">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="space-y-3 mt-2">
              {(displayScheduled || [])
                .filter(
                  (p) =>
                    dialogDate &&
                    dayjs(p.scheduledTime).isSame(dialogDate, "day")
                )
                .map((p) => (
                  <div key={p._id} className="card-modern p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <img
                          src={
                            p.thumbnailUrl ||
                            (p.mediaUrls && p.mediaUrls[0]) ||
                            p.mediaUrl ||
                            "/placeholder-image.png"
                          }
                          alt="preview"
                          className="w-12 h-12 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/placeholder-image.png";
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <Typography
                            variant="body2"
                            className="text-sm font-medium text-gray-900 dark:text-white truncate"
                            title={p.caption || "(no caption)"}
                          >
                            {p.caption || "(no caption)"}
                          </Typography>
                          <Typography className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {new Date(p.scheduledTime).toLocaleString()}
                          </Typography>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Tooltip
                          title={p.errorMessage || ""}
                          disableHoverListener={!p.errorMessage}
                        >
                          <Chip
                            size="small"
                            label={p.status}
                            className={`text-xs font-semibold ${
                              p.status === "PUBLISHED"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                : p.status === "FAILED"
                                ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                            }`}
                          />
                        </Tooltip>
                        {p.status === "PENDING" && !p._id.startsWith("mock-") && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => startEdit(p)}
                              className="text-xs px-3 py-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 normal-case"
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDelete(p._id)}
                              className="text-xs px-3 py-1 border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 normal-case"
                            >
                              Delete
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handlePublishNow(p._id)}
                              className="text-xs px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white normal-case"
                            >
                              Publish Now
                            </Button>
                          </div>
                        )}
                        {p._id.startsWith("mock-") && (
                          <div className="flex items-center gap-2">
                            <Button size="small" disabled className="text-xs px-3 py-1 normal-case">
                              Edit
                            </Button>
                            <Button size="small" disabled className="text-xs px-3 py-1 normal-case">
                              Delete
                            </Button>
                            <Button size="small" disabled className="text-xs px-3 py-1 normal-case">
                              Publish Now
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {editingPost && editingPost._id === p._id && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-4">
                          <TextField
                            label="Caption"
                            fullWidth
                            value={editCaption}
                            onChange={(e) => setEditCaption(e.target.value)}
                            className="bg-white dark:bg-gray-800"
                          />
                          <DateTimePicker
                            label="Schedule Time"
                            value={editTime}
                            onChange={setEditTime}
                            renderInput={(params) => (
                              <TextField {...params} fullWidth className="bg-white dark:bg-gray-800" />
                            )}
                            minDateTime={new Date()}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => setEditingPost(null)}
                              disabled={savingEdit}
                              className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={saveEdit}
                              disabled={savingEdit}
                              className="bg-primary-600 hover:bg-primary-700 text-white normal-case"
                            >
                              {savingEdit ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions className="px-6 pb-6 pt-2">
          <Button
            onClick={() => setDialogOpen(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
